import { store } from "@app/store";
import { Block } from "@components/block";
import { Input } from "@components/input";
import { CustomSelect } from "@components/select";
import { Control, useForm } from "react-hook-form";
import { Button } from "@components/button";
import styles from "./selectedUser.module.scss";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, GridCol } from "@components/grid";
import { copyToClipboard } from "@helpers/utilities";
import { useBoolState } from "@hooks/use-bool-state";
import {
  UserLoginChangeModal,
  UserPasswordChangeModal,
  User2FAChangeModal,
  UserTGGroupModal,
  UserSetRoleModal,
} from "@components/userModals/userChangeModal";
import { WhitelistWalletsModal } from "@components/userModals/whitelist";
import { useEffect, useRef } from "react";
import { useAdminUsers } from "@features/admin/users";
import {
  AdminUserTypes,
  LeadUserInfoUpdateRequest,
  MerchantUserInfoUpdateRequest,
  TraderUserInfoUpdateRequest,
  UserInfoUpdateRequest,
} from "@features/admin/users/api";
import { FlexLoader } from "@components/loader";
import MultiSelect from "@components/multiSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import { resetData } from "@features/admin/users/slice";
import { useAuth } from "@features/auth";
import { EllipsisText } from "@components/ellipsis-text";
import { TraderBlock } from "./users/traderBlock";
import { MerchantBlock } from "./users/merchantBlock";
import { PlatformBlock } from "./users/platformBlock";
import { LeadBlock } from "./users/leadBlock";
import { getUpdateSchema } from "./schemas";
import { SetTeamleadComissionModal } from "./users/platformModals";

export const SelectedUserPage = () => {
  const { selectedUserId } = useParams();
  const [isLoginModalOpen, showLoginModal, hideLoginModal] =
    useBoolState(false);
  const [isPasswordModalOpen, showPasswordModal, hidePasswordModal] =
    useBoolState(false);
  const [is2FAModalOpen, show2FAModal, hide2FAModal] = useBoolState(false);
  const [isWhitelistModalOpen, showWhitelistModal, hideWhitelistModal] =
    useBoolState(false);
  // const [isActivateModalOpen, showActivateModal, hideActivateModal] =
  //   useBoolState(false);
  const [
    isTeamleadComissionModalOpen,
    showTeamleadComissionModal,
    hideTeamleadComissionModal,
  ] = useBoolState(false);

  const [isSetRoleModalOpen, showSetRoleModal, hideSetRoleModal] =
    useBoolState(false);
  const [isTGGroupModalOpen, showTGGroupModal, hideTGGroupModal] =
    useBoolState(false);
  const navigate = useNavigate();

  // const [roles, setRoles] = useState<string[]>([]);

  const { control, handleSubmit, reset, getValues } =
    useForm<UserInfoUpdateRequest>({
      defaultValues: {
        // status: "",
        commission_config: {
          commission_sbp: [],
          commission_card: [],
          commission_account_number: [],
        },
      },
      resolver: async (data, context, options) => {
        const schema = getUpdateSchema(state.userInfo?.role ?? "");
        return yupResolver(schema)(data, context, options as never);
      },
    });

  const userRoleOptions = [
    { text: "merchant", value: "merchant" },
    { text: "merchant Agent", value: "merchant_agent" },
    { text: "Trader", value: "trader" },
    { text: "Trader Teamlead", value: "trader_teamlead" },
    { text: "Administrator", value: "administrator" },
    { text: "Customer Support", value: "customer_support" },
    { text: "Super User", value: "super_user" },
  ];
  const statusOptions = [
    { text: "Активен", value: "active", disabled: true },
    { text: "Заморожен", value: "frozen" },
    { text: "Заблокирован", value: "blocked" },
    { text: "Не активен", value: "inactive" },
    { text: "Отказ в активации", value: "rejected" },
  ];

  const {
    state,
    getAdminUserInfo,
    updateAdminUserInfo,
    activateUser,
    getApiKey,
  } = useAdminUsers();
  const { isAdmin, isSuperUser } = useAuth();

  // useEffect(() => {
  //   const sub = watch((value) => {
  //     if (value.roles) {
  //       setRoles(value.roles.filter((val) => val != null) as string[]);
  //     }
  //   });
  //   return () => sub.unsubscribe();
  // }, [watch]);

  useEffect(() => {
    if (!state.userInfo) {
      reset();
      // setRoles([]); // If local state
      return;
    }

    // Compute form data with common fields
    const formData: Partial<UserInfoUpdateRequest> = {
      roles: state.userInfo.role ? [state.userInfo.role] : [],
      status: state.userInfo.status ?? "",
      // pay_in: state.userInfo.pay_in ?? false,
      // pay_out: state.userInfo.pay_out ?? false,
      // min_sum_order: state.userInfo.min_sum_order ?? 0,
      // max_sum_order: state.userInfo.max_sum_order ?? 0,
      // min_sum_pay_out_order: state.userInfo.min_sum_pay_out_order ?? 0,
      // max_sum_pay_out_order: state.userInfo.max_sum_pay_out_order ?? 0,
      // referrer_email: state.userInfo.referrer_email ?? "",
      // commission_config: state.userInfo.commission_config
      //   ? {
      //       commission_account_number:
      //         state.userInfo.commission_config.commission_account_number ?? [],
      //       commission_sbp:
      //         state.userInfo.commission_config.commission_sbp ?? [],
      //       commission_card:
      //         state.userInfo.commission_config.commission_card ?? [],
      //     }
      //   : undefined,
    };

    if (
      state.userInfo.kind == AdminUserTypes.Trader ||
      state.userInfo.kind == AdminUserTypes.Merchant
    ) {
      (formData as Partial<TraderUserInfoUpdateRequest>).pay_in = (
        state.userInfo.pay_in ?? false
      ).toString() as unknown as boolean;
      (formData as Partial<TraderUserInfoUpdateRequest>).pay_out = (
        state.userInfo.pay_out ?? false
      ).toString() as unknown as boolean;
      (formData as Partial<TraderUserInfoUpdateRequest>).min_sum_order =
        state.userInfo.min_sum_order ?? 0;
      (formData as Partial<TraderUserInfoUpdateRequest>).max_sum_order =
        state.userInfo.max_sum_order ?? 0;
      (formData as Partial<TraderUserInfoUpdateRequest>).min_sum_pay_out_order =
        state.userInfo.min_sum_pay_out_order ?? 0;
      (formData as Partial<TraderUserInfoUpdateRequest>).max_sum_pay_out_order =
        state.userInfo.max_sum_pay_out_order ?? 0;
      (formData as Partial<TraderUserInfoUpdateRequest>).referrer_email =
        state.userInfo.referrer_email ?? "";
      (formData as Partial<TraderUserInfoUpdateRequest>).commission_config =
        state.userInfo.commission_config
          ? {
              commission_account_number:
                state.userInfo.commission_config.commission_account_number ??
                [],
              commission_sbp:
                state.userInfo.commission_config.commission_sbp ?? [],
              commission_card:
                state.userInfo.commission_config.commission_card ?? [],
              commission_ibt:
                state.userInfo.commission_config.commission_ibt ?? [],
            }
          : undefined;
      (
        formData as Partial<TraderUserInfoUpdateRequest>
      ).team_lead_commission_pay_in =
        state.userInfo.team_lead_commission_pay_in ?? 0;
    }

    // Add trader-specific fields
    if (state.userInfo.kind == AdminUserTypes.Trader) {
      (formData as Partial<TraderUserInfoUpdateRequest>).kind =
        AdminUserTypes.Trader;
      (formData as Partial<TraderUserInfoUpdateRequest>).boosted = (
        state.userInfo.boosted ?? false
      ).toString() as unknown as boolean;
      (formData as Partial<TraderUserInfoUpdateRequest>).allow_farm_creation = (
        state.userInfo.allow_farm_creation ?? false
      ).toString() as unknown as boolean;
      (
        formData as Partial<TraderUserInfoUpdateRequest>
      ).allow_requisites_without_device = (
        state.userInfo.allow_requisites_without_device ?? false
      ).toString() as unknown as boolean;
      // (formData as Partial<TraderUserInfoUpdateRequest>).team_lead_commission_pay_out =
      //   state.userInfo.team_lead_commission_pay_out ?? 0;
    }

    // Add merchant-specific fields
    if (state.userInfo.kind == AdminUserTypes.Merchant) {
      (formData as Partial<MerchantUserInfoUpdateRequest>).kind =
        AdminUserTypes.Merchant;
      (
        formData as Partial<MerchantUserInfoUpdateRequest>
      ).commission_withdrawal = state.userInfo.commission_withdrawal ?? 0;
      (
        formData as Partial<MerchantUserInfoUpdateRequest>
      ).commission_for_trader_pay_out =
        state.userInfo.commission_for_trader_pay_out ?? 0;
      (formData as Partial<MerchantUserInfoUpdateRequest>).commission_pay_out =
        state.userInfo.commission_pay_out ?? 0;
    }

    // Add lead-specific fields (example; adjust as needed)
    if (state.userInfo.kind == AdminUserTypes.Lead) {
      (formData as Partial<LeadUserInfoUpdateRequest>).kind =
        AdminUserTypes.Lead;
      (formData as Partial<LeadUserInfoUpdateRequest>).commission_withdrawal =
        state.userInfo.commission_withdrawal ?? 0;
      // Add any other lead fields...
    }

    // Reset the form with computed data
    reset(formData);

    // Update local roles state if needed
    // setRoles(formData.roles ?? []);
  }, [state.userInfo, reset]);

  useEffect(() => {
    store.dispatch(resetData());
    if (selectedUserId == null) navigate("/");
    else {
      getAdminUserInfo(selectedUserId);
    }
  }, [getAdminUserInfo, selectedUserId]);

  const prevHasMerchantRole = useRef<boolean>(false);

  useEffect(() => {
    const hasMerchantRole =
      state.userInfo?.kind == AdminUserTypes.Merchant &&
      isSuperUser &&
      selectedUserId;
    if (hasMerchantRole && !prevHasMerchantRole.current && selectedUserId) {
      getApiKey(selectedUserId);
    }
    prevHasMerchantRole.current = !!hasMerchantRole;
  }, [isSuperUser, selectedUserId, state.userInfo?.kind, getApiKey]);

  const onActivateUser = () => {
    if (selectedUserId) {
      const user_id = selectedUserId;
      activateUser({ user_id }).then(() => {
        getAdminUserInfo(user_id);
        if (isSuperUser && state.userInfo?.kind === AdminUserTypes.Merchant) {
          getApiKey(user_id);
        }
      });
    }
  };

  const onSubmit = (data: UserInfoUpdateRequest) => {
    const oldStatus = data.status;
    if (oldStatus == "active") {
      data.status = undefined;
    }
    if (selectedUserId) {
      updateAdminUserInfo(selectedUserId, data).then(() => {
        if (oldStatus == "active") {
          data.status = oldStatus;
        }
        // store.dispatch(updateData(data));
        getAdminUserInfo(selectedUserId);
      });
    }
  };
  const submitLeadData = () => {
    if (selectedUserId) {
      const comission = getValues("commission_withdrawal");
      updateAdminUserInfo(selectedUserId, {
        commission_withdrawal: +(comission ?? 0),
      } as UserInfoUpdateRequest).then(() => {
        getAdminUserInfo(selectedUserId);
      });
    }
  };

  const signIn = () => {
    const role =
      state.userInfo?.role == "trader_teamlead"
        ? "teamlead"
        : state.userInfo?.role == "merchant_agent"
          ? "agent"
          : state.userInfo?.role == "trader"
            ? "trader"
            : state.userInfo?.role == "merchant"
              ? "merchant"
              : null;
    if (state.userInfo != null && role) {
      const url = `${window.origin}/${selectedUserId}/${role}`;
      window.open(url, "_blank")?.focus();
    }
  };

  const isHasRole = state.userInfo?.role != null;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Block className={styles.block}>
          <Button
            buttonType="button"
            className={styles.backButton}
            onClick={() => navigate("/")}
          >
            Назад
          </Button>
          {state.loading ? (
            <FlexLoader />
          ) : (
            <div className={styles.headerSmall}>
              <Grid grow={false}>
                <GridCol span={2}>
                  <div>Почта пользователя:</div>
                  <div
                    onClick={() =>
                      state.userInfo?.email &&
                      copyToClipboard(state.userInfo.email)
                    }
                    className={styles.email}
                  >
                    {state.userInfo?.email}
                  </div>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    disabled={!isAdmin || !isHasRole}
                    onClick={signIn}
                  >
                    Войти
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    onClick={showLoginModal}
                    disabled={!isSuperUser}
                  >
                    Изм логин (TODO)
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    onClick={showPasswordModal}
                    disabled={!isSuperUser}
                  >
                    Изм Пароль
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    onClick={show2FAModal}
                    disabled={!isSuperUser}
                  >
                    Сброс 2FA
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <Button buttonType="button">Найти сессию (TODO)</Button>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    onClick={showSetRoleModal}
                    disabled={!isSuperUser || !isAdmin || isHasRole}
                  >
                    Установить роль
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <MultiSelect
                    control={control}
                    name="roles"
                    options={userRoleOptions}
                    placeholder="Выберите роли"
                    disabled
                  />
                  <Button
                    buttonType="button"
                    style={{ marginTop: "3px" }}
                    onClick={onActivateUser}
                    disabled={!isAdmin || !isHasRole}
                  >
                    Активировать
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <CustomSelect
                    control={control}
                    options={statusOptions}
                    name="status"
                    label="Статус"
                    placeholder="Статус"
                    disabled={!isAdmin || !isHasRole}
                  ></CustomSelect>
                </GridCol>
                <GridCol span={2}>
                  <Button
                    buttonType="button"
                    onClick={showWhitelistModal}
                    disabled={
                      state.userInfo == null ||
                      ![
                        "merchant",
                        "trader_teamlead",
                        "merchant_agent",
                      ].includes(state.userInfo.role ?? "") ||
                      !isSuperUser
                    }
                  >
                    Вайтлист кошельков
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  <span>{`ТГ ID группы: ${state.userInfo?.tg_group_id ?? "null"}`}</span>
                  {!state.userInfo?.tg_group_id && (
                    <span style={{ color: "red" }}>Обязательное поле!</span>
                  )}
                  <Button
                    buttonType="button"
                    disabled={!isAdmin || !isHasRole}
                    onClick={showTGGroupModal}
                  >
                    Изменить
                  </Button>
                </GridCol>
                <GridCol span={2}>
                  {state.userInfo?.kind == "platform" ? (
                    <Button
                      onClick={showTeamleadComissionModal}
                      buttonType="button"
                    >
                      Задать комиссию агенту
                    </Button>
                  ) : (
                    <Input
                      name="referrer_email"
                      control={control}
                      placeholder="Email тимлид"
                      label="Email тимлид"
                      disabled={!isAdmin}
                    ></Input>
                  )}
                </GridCol>
                {isHasRole &&
                  isSuperUser &&
                  (state.userInfo?.kind == AdminUserTypes.Merchant ||
                    state.userInfo?.kind == AdminUserTypes.Lead) && (
                    <GridCol span={2}>
                      <Input
                        name="commission_withdrawal"
                        control={control}
                        placeholder="Комиссия за вывод"
                        label="Комиссия за вывод"
                        disabled={!isAdmin}
                        type="number"
                      ></Input>
                    </GridCol>
                  )}
              </Grid>
              {isHasRole &&
                isSuperUser &&
                state.userInfo?.kind == AdminUserTypes.Merchant && (
                  <div>
                    Api key:{" "}
                    <EllipsisText
                      startLength={200}
                      endLength={200}
                      text={state.apiKey ?? ""}
                      copy
                    />{" "}
                  </div>
                )}
            </div>
          )}
        </Block>
        {isHasRole && state.userInfo?.kind == AdminUserTypes.Trader && (
          <TraderBlock control={control} />
        )}
        {isHasRole && state.userInfo?.kind == AdminUserTypes.Merchant && (
          <MerchantBlock control={control} />
        )}
        {isHasRole && state.userInfo?.kind == AdminUserTypes.Lead && (
          <LeadBlock />
        )}
        {isHasRole && state.userInfo?.kind == AdminUserTypes.Platform && (
          <PlatformBlock />
        )}
        {AdminUserTypes.Lead == state.userInfo?.kind && (
          <Block>
            <Button buttonType="button" onClick={submitLeadData}>
              Сохранить
            </Button>
          </Block>
        )}
        {!["trader_teamlead", "merchant_agent", "platform"].includes(
          state.userInfo?.role ?? ""
        ) && (
          <Block>
            <Button
              buttonType="submit"
              disabled={
                !isAdmin ||
                (!state.userInfo?.tg_group_id &&
                  !["trader_teamlead", "merchant_agent"].includes(
                    state.userInfo?.role ?? ""
                  ))
              }
            >
              Сохранить
            </Button>
          </Block>
        )}
      </form>
      <UserLoginChangeModal
        isModalOpen={isLoginModalOpen}
        hideModal={hideLoginModal}
      />
      {selectedUserId && (
        <>
          <UserPasswordChangeModal
            isModalOpen={isPasswordModalOpen}
            hideModal={hidePasswordModal}
            userId={selectedUserId}
          />
          <User2FAChangeModal
            isModalOpen={is2FAModalOpen}
            hideModal={hide2FAModal}
            userId={selectedUserId}
          />
          {(state.userInfo?.kind == AdminUserTypes.Merchant ||
            state.userInfo?.kind == AdminUserTypes.Lead) && (
            <WhitelistWalletsModal
              isModalOpen={isWhitelistModalOpen}
              hideModal={hideWhitelistModal}
              wallets={state.userInfo?.wallets ?? []}
              userId={selectedUserId}
            />
          )}
        </>
      )}
      <UserSetRoleModal
        isModalOpen={isSetRoleModalOpen}
        hideModal={hideSetRoleModal}
        userId={selectedUserId ?? ""}
      />
      <UserTGGroupModal
        isModalOpen={isTGGroupModalOpen}
        hideModal={hideTGGroupModal}
        userId={selectedUserId ?? ""}
      />
      <SetTeamleadComissionModal
        isOpen={isTeamleadComissionModalOpen}
        onClose={hideTeamleadComissionModal}
      />
    </>
  );
};

export type UserBlockProps = {
  control: Control<UserInfoUpdateRequest>;
  // user: User;
};

// type MerchantBlockProps = { user: User }
